import React, { useState, useEffect, useRef } from 'react';

// Dept Wash — per-department Plan vs Actual view (cartons + function hours/CPLH +
// trailer plans + OB fill), styled after the Cost/Volume Execution snip. One
// department is shown at a time via the dropdown. Reuses the same caches and
// math as the MIDI tab.

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PROD_DOORS = {
  PID: ['DD352','DD353','DD354','DD355','DD356','DD357','DD358','DD359','DD360'],
  PR: ['DD143','DD146','DD150','DD153','DD156'],
  SKID: ['DD135','DD136','DD137','DD138'],
  MANR: ['DD347','DD348'],
};
// AROS function split per OLM1 definition:
//   IB AROS  = Unload, Operator, CSX Label
//   OB AROS  = Avery, Divert, Sort, Waterspider (outbound; part of fused EOL/AROS)
const IB_AROS_PATHS = ['AROS Unload', 'AROS Operator', 'AROS CSX Label'];

function extractCartons(notes) {
  const m = (notes || '').match(/IBVEND\s+\w+\s+(\d+)/i);
  return m ? parseInt(m[1]) : 0;
}

// Department definitions: which carton rows, function groups, trailer paths, and
// OB fill categories each dept shows.
const DEPTS = {
  'PID': {
    label: 'PID',
    cartons: ['pid', 'aros'],
    groups: [ { g: 'PID' }, { g: 'AROS', arosSide: 'ib', label: 'IB AROS' }, { g: 'IB Support' } ],
    paths: ['PID', 'MANR'],
  },
  'IB PR': {
    label: 'IB PR',
    cartons: ['pr'],
    groups: [ { g: 'IB PR' }, { g: 'IB PIT' } ],
    paths: ['PR', 'SKID'],
  },
  'Fluid': {
    label: 'Fluid',
    cartons: ['fluid'],
    groups: [ { g: 'Fluid' }, { g: 'OB Support' } ],
    paths: [],
    fill: ['Fluid Direct', 'Fluid Offline'],
  },
  'EOL & OB AROS': {
    label: 'EOL & OB AROS',
    cartons: ['eol', 'aros'],
    groups: [ { g: 'EOL' }, { g: 'AROS', arosSide: 'ob', label: 'OB AROS' } ],
    paths: [],
    // Fuse EOL Sort + AROS Sort into a single "Manual Sort" row (their labor shares
    // the IDRT tracking code and is no longer separable). The other functions stay
    // differentiated by their own carton stream (EOL fns vs eol, OB AROS fns vs aros).
    manualSort: true,
  },
  'OBPR': {
    label: 'OBPR',
    cartons: ['obpr'],
    groups: [ { g: 'OBPR' } ],
    paths: [],
    fill: ['Palletized'],
  },
};

export default function DeptWash({ api, site }) {
  const [date, setDate] = useState('');
  const [shiftType, setShiftType] = useState('day');
  const [dept, setDept] = useState('PID');
  const [yardData, setYardData] = useState(null);
  const [lvpPlan, setLvpPlan] = useState(null);
  const [fclmData, setFclmData] = useState(null);
  const [midiPlan, setMidiPlan] = useState(null);
  const [midiActual, setMidiActual] = useState(null);
  const [obFill, setObFill] = useState(null);
  const [bridge, setBridge] = useState('');
  const [saved, setSaved] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    const d = new Date();
    const pst = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    if (pst.getHours() < 6) pst.setDate(pst.getDate() - 1);
    setDate(`${pst.getFullYear()}-${String(pst.getMonth()+1).padStart(2,'0')}-${String(pst.getDate()).padStart(2,'0')}`);
  }, []);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    const dObj = new Date(date + 'T12:00:00');
    const dayOfWeek = dObj.getDay();
    const sunday = new Date(dObj); sunday.setDate(dObj.getDate() - dayOfWeek);
    const weekStart = `${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`;
    const shiftKey = `${DAYS[dayOfWeek]}_${shiftType}`;

    Promise.all([
      fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_YARD&shift_type=current`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_PLAN_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_ACTUAL_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=OB_FILL_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
    ]).then(([yard, lvp, fclm, midiP, midiA, fill]) => {
      setYardData(yard?.trailers ? yard : null);
      setMidiPlan(midiP?.planTrailers ? midiP : null);
      setMidiActual(midiA?.completions ? midiA : null);
      setObFill(fill?.trailers ? fill.trailers : null);
      if (lvp?.shiftData) {
        const plan = lvp.shiftData[shiftKey] || {};
        const fpp = lvp.fppRatios || {};
        const totalCartons = plan.cartons || 0;
        const pidC = plan.pidCartons ? parseInt(plan.pidCartons) : Math.round(totalCartons * (fpp.pid || 0));
        const arosC = plan.arosCartons ? parseInt(plan.arosCartons) : Math.round(totalCartons * (fpp.aros || 0));
        const prC = totalCartons - pidC - arosC;
        setLvpPlan({ totalCartons, pidCartons: pidC, arosCartons: arosC, prCartons: prC, skidCartons: Math.round(totalCartons * (fpp.skid || 0)), shiftKey, eolPct: lvp.eolPct || 33, cpp: lvp.cpp || 30.98, computedHours: lvp.computedHours });
      } else setLvpPlan(null);
      setFclmData(fclm?.labor ? fclm : null);
      setLoading(false);
    });
  }, [api, site, date, shiftType]);

  // Cost Bridge is per-dept — refetch whenever the dept (or date/shift) changes.
  useEffect(() => {
    if (!date) return;
    const key = `DEPTWASH_BRIDGE_${date}_${dept.replace(/[^A-Za-z0-9]/g,'')}`;
    fetch(`${api}/api/fclm/cache?site=${site}&date=${key}&shift_type=${shiftType}`)
      .then(r => r.json())
      .then(b => setBridge((b && typeof b.bridge === 'string') ? b.bridge : ''))
      .catch(() => setBridge(''));
  }, [api, site, date, shiftType, dept]);

  const saveBridge = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: `DEPTWASH_BRIDGE_${date}_${dept.replace(/[^A-Za-z0-9]/g,'')}`, shift_type: shiftType, data: { bridge } }),
    });
    setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const getActualCartons = (key) => {
    if (key === 'fluid') return (fclmData?.cartons?.fluidDirect?.total || 0) + (fclmData?.cartons?.fluidOffline?.total || 0);
    if (key === 'obpr') return fclmData?.cartons?.pit?.total || 0;
    return fclmData?.cartons?.[key]?.total || 0;
  };
  const cartonGoal = (key) => {
    if (!lvpPlan) return 0;
    switch (key) {
      case 'pid': return lvpPlan.pidCartons || 0;
      case 'aros': return lvpPlan.arosCartons || 0;
      case 'pr': return lvpPlan.prCartons || 0;
      case 'obpr': return lvpPlan.prCartons || 0;
      case 'eol': return Math.round((lvpPlan.pidCartons || 0) * (lvpPlan.eolPct || 33) / 100);
      case 'fluid': return Math.round(lvpPlan.pidCartons * (1 - (lvpPlan.eolPct || 33)/100)) + Math.round(lvpPlan.pidCartons * (lvpPlan.eolPct||33)/100) + Math.round(lvpPlan.arosCartons);
      default: return lvpPlan.totalCartons || 0;
    }
  };
  const cartonLabel = { pid: 'PID Cartons', aros: 'AROS Cartons', pr: 'PR Cartons', obpr: 'OBPR Cartons', eol: 'EOL Cartons', fluid: 'Fluid Cartons' };
  const planCartonForGroup = (g) => {
    if (!lvpPlan) return 0;
    const m = { 'PID': lvpPlan.pidCartons, 'AROS': lvpPlan.arosCartons, 'IB PR': lvpPlan.prCartons, 'IB PIT': lvpPlan.prCartons, 'EOL': Math.round((lvpPlan.pidCartons||0)*(lvpPlan.eolPct||33)/100), 'Fluid': lvpPlan.totalCartons, 'OBPR': lvpPlan.prCartons, 'IB Support': lvpPlan.totalCartons, 'OB Support': lvpPlan.totalCartons };
    return m[g] || lvpPlan.totalCartons || 0;
  };
  const cartonForGroup = (g) => {
    const m = { 'PID': 'pid', 'AROS': 'aros', 'IB PR': 'pr', 'IB PIT': 'pit', 'EOL': 'eol', 'Fluid': 'outbound', 'OBPR': 'pit', 'IB Support': 'inbound', 'OB Support': 'outbound' };
    return getActualCartons(m[g] || 'inbound');
  };

  const cell = { border: '1px solid #ccc', padding: '3px 8px', fontSize: '0.7rem', textAlign: 'center' };
  const hdr = { ...cell, background: '#f0f0f0', fontWeight: 700 };
  const labelBlk = { ...cell, background: '#d8d2cb', fontWeight: 700, writingMode: 'horizontal-tb', width: 90 };
  const cellStyle = { border: '1px solid #ddd', padding: '3px 6px', fontSize: '0.65rem', textAlign: 'center' };
  const headerStyle = { padding: '3px 6px', fontSize: '0.6rem', textAlign: 'center' };
  const normalizeVid = (vid) => String(vid || '').replace(/^V/, '');
  const evtUrl = (visitId) => { const v = normalizeVid(visitId); if (!v) return null; const to = Date.now(); const from = to - 30 * 86400000; return `https://trans-logistics.amazon.com/yms/eventHistory#/eventReport?yard=${site}&fromDate=${from}&toDate=${to}&visitId=${v}`; };
  const idLink = (text, visitId) => { const u = evtUrl(visitId); return u ? <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{text}</a> : text; };
  const fclmToLvp = { 'Offline Wallbuild': 'Wall Builder', 'Sorter Wallbuild': 'Sorter Wallbuilder', 'Wrap/Lantech': 'Pallet Wrap/Lantech', 'Fluid WS': 'Fluid WS', 'Fluid Load': 'Fluid Load', 'Jam Clear': 'Jam Clear' };

  const copyImage = async () => {
    const src = captureRef.current; if (!src) return;
    try {
      const clone = src.cloneNode(true);
      const srcTAs = src.querySelectorAll('textarea');
      clone.querySelectorAll('textarea').forEach((ta, i) => {
        const div = document.createElement('div');
        div.textContent = srcTAs[i] ? srcTAs[i].value : ta.value;
        div.setAttribute('style', 'white-space:pre-wrap;font-size:0.85rem;padding:6px;border:1px solid #ccc;border-radius:4px;min-height:60px;background:#fff;');
        ta.parentNode.replaceChild(div, ta);
      });
      const rect = src.getBoundingClientRect();
      const w = Math.ceil(rect.width), h = Math.ceil(clone.scrollHeight || rect.height);
      clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
      const html = new XMLSerializer().serializeToString(clone);
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><foreignObject width='100%' height='100%'><div xmlns='http://www.w3.org/1999/xhtml' style='background:#fff;font-family:Arial,sans-serif'>${html}</div></foreignObject></svg>`;
      const img = new Image();
      const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = svgUrl; });
      const dpr = window.devicePixelRatio || 1;
      const cv = document.createElement('canvas'); cv.width = w * dpr; cv.height = h * dpr;
      const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr); ctx.fillStyle = '#fff'; ctx.fillRect(0,0,w,h); ctx.drawImage(img, 0, 0);
      cv.toBlob(async (blob) => {
        try { await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]); setCopyMsg('✅ Copied!'); }
        catch { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `deptwash_${site}_${date}_${dept}.png`; a.click(); setCopyMsg('⬇️ Downloaded'); }
        setTimeout(() => setCopyMsg(''), 3000);
      });
    } catch (e) { setCopyMsg('❌ ' + e.message); setTimeout(() => setCopyMsg(''), 4000); }
  };

  const def = DEPTS[dept];

  // Trailer plan per path (frozen MIDI_PLAN capped by LVP cartons; MIDI_ACTUAL completions)
  const getPlannedCartons = (path) => ({ PID: lvpPlan?.pidCartons, PR: lvpPlan?.prCartons, MANR: lvpPlan?.arosCartons, SKID: lvpPlan?.skidCartons })[path] || 0;
  const getCompletions = (path) => (midiActual?.completions || []).filter(c => c.path === path);
  const planTrailersFor = (path) => {
    if (!midiPlan?.planTrailers) return [];
    const pathTrailers = midiPlan.planTrailers.filter(t => t.path === path && extractCartons(t.notes) > 0).sort((a,b) => (b.dwellHours||0)-(a.dwellHours||0));
    const planned = getPlannedCartons(path); let running = 0; const out = [];
    for (const t of pathTrailers) { const c = extractCartons(t.notes); out.push({ ...t, cartons: c }); running += c; if (planned > 0 && running >= planned) break; }
    return out;
  };

  return (
    <div>
      <h2 className="page-title">Dept Wash</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Dept: <select value={dept} onChange={e => setDept(e.target.value)} style={{ fontSize: '0.75rem' }}>{Object.keys(DEPTS).map(k => <option key={k} value={k}>{DEPTS[k].label}</option>)}</select></label>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <div>{['day','night'].map(s => <button key={s} className={`btn ${shiftType===s?'btn-primary':'btn-secondary'}`} onClick={() => setShiftType(s)} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>{s === 'day' ? 'Day' : 'Night'}</button>)}</div>
        <button className="btn btn-primary" onClick={saveBridge} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>{saved ? '✅ Saved!' : '💾 Save Bridge'}</button>
        <button className="btn" onClick={copyImage} disabled={loading} style={{ background: '#455a64', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.7rem' }}>📋 Copy Image</button>
        {copyMsg && <span style={{ fontSize: '0.7rem' }}>{copyMsg}</span>}
      </div>

      {loading && <div className="card">Loading…</div>}
      {!loading && !fclmData && <div className="card">No FCLM data for this date/shift.</div>}

      {!loading && fclmData && (
        <div ref={captureRef}>
          <div className="card" style={{ overflowX: 'auto', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <div style={{ ...labelBlk, display: 'flex', alignItems: 'center', textAlign: 'center', padding: 8 }}>{def.label}<br/>Cost/Volume Execution</div>
              <div>
                {/* Volume table */}
                <table style={{ borderCollapse: 'collapse', marginBottom: 10 }}>
                  <thead><tr><th style={hdr}>Metric</th><th style={hdr}>BB Goal</th><th style={hdr}>Actual</th><th style={hdr}>% to BB</th><th style={hdr}>Carton +/-</th></tr></thead>
                  <tbody>
                    {def.cartons.map(k => {
                      const goal = cartonGoal(k), act = getActualCartons(k);
                      const pct = goal > 0 ? Math.round(act/goal*100) : null;
                      const diff = act - goal;
                      const rowEl = (
                        <tr key={k}>
                          <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{cartonLabel[k] || k}</td>
                          <td style={cell}>{goal ? goal.toLocaleString() : '—'}</td>
                          <td style={cell}>{act ? act.toLocaleString() : '—'}</td>
                          <td style={{ ...cell, background: pct != null ? (pct >= 100 ? '#e2efda' : '#fbe4e4') : undefined }}>{pct != null ? `${pct}%` : '—'}</td>
                          <td style={{ ...cell, background: goal ? (diff >= 0 ? '#e2efda' : '#fbe4e4') : undefined }}>{goal ? (diff > 0 ? '+' : '') + diff.toLocaleString() : '—'}</td>
                        </tr>
                      );
                      // For Fluid, break the total into Direct + Offline sub-rows beneath it.
                      if (k === 'fluid') {
                        const dAct = fclmData?.cartons?.fluidDirect?.total || 0;
                        const oAct = fclmData?.cartons?.fluidOffline?.total || 0;
                        // Direct = non-EOL PID + AROS; Offline = EOL portion of PID.
                        const dGoal = lvpPlan ? Math.round(lvpPlan.pidCartons * (1 - (lvpPlan.eolPct||33)/100)) : 0;
                        const oGoal = lvpPlan ? Math.max(0, (lvpPlan.pidCartons||0) + (lvpPlan.arosCartons||0) - dGoal) : 0;
                        const subRow = (lbl, g, a) => {
                          const p = g > 0 ? Math.round(a/g*100) : null; const df = a - g;
                          return (
                            <tr key={lbl}>
                              <td style={{ ...cell, textAlign: 'left', paddingLeft: 20, color: '#555' }}>{lbl}</td>
                              <td style={cell}>{g ? g.toLocaleString() : '—'}</td>
                              <td style={cell}>{a ? a.toLocaleString() : '—'}</td>
                              <td style={{ ...cell, background: p != null ? (p >= 100 ? '#e2efda' : '#fbe4e4') : undefined }}>{p != null ? `${p}%` : '—'}</td>
                              <td style={{ ...cell, background: g ? (df >= 0 ? '#e2efda' : '#fbe4e4') : undefined }}>{g ? (df > 0 ? '+' : '') + df.toLocaleString() : '—'}</td>
                            </tr>
                          );
                        };
                        return [rowEl, subRow('Fluid Direct', dGoal, dAct), subRow('Fluid Offline', oGoal, oAct)];
                      }
                      return rowEl;
                    })}
                  </tbody>
                </table>
                {/* Function hours / CPLH table */}
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr><th style={hdr}>Metric</th><th style={hdr}>Goal Hours</th><th style={hdr}>Shift Plan CPLH</th><th style={hdr}>Actual Hours</th><th style={hdr}>Actual CPLH</th><th style={hdr}>Hours to plan</th><th style={hdr}>CPLH % to plan</th></tr></thead>
                  <tbody>
                    {def.groups.map(gd => {
                      const group = gd.g;
                      let groupFns = (fclmData.labor || []).filter(l => l.group === group);
                      // OBPR on Dept Wash excludes the TO Palletize line entirely —
                      // drop it so it neither renders nor counts toward Actual Hours.
                      if (group === 'OBPR') groupFns = groupFns.filter(l => l.process_path !== 'TO Palletize');
                      if (group === 'AROS' && gd.arosSide === 'ib') groupFns = groupFns.filter(l => IB_AROS_PATHS.includes(l.process_path));
                      if (group === 'AROS' && gd.arosSide === 'ob') groupFns = groupFns.filter(l => !IB_AROS_PATHS.includes(l.process_path));
                      // Manual Sort fusion: EOL Sort + AROS Sort share the IDRT tracking
                      // code and are no longer separable, so present them as ONE synthetic
                      // "Manual Sort" row (hours summed). Drop the raw sort paths from both
                      // groups; attach the fused row to the EOL group. Its carton basis is
                      // the combined eol+aros total (handled in FN_CARTON_BASIS below).
                      if (def.manualSort) {
                        groupFns = groupFns.filter(l => l.process_path !== 'EOL Sort' && l.process_path !== 'AROS Sort');
                        if (group === 'EOL') {
                          const sortRows = (fclmData.labor || []).filter(l => l.process_path === 'EOL Sort' || l.process_path === 'AROS Sort');
                          if (sortRows.length) {
                            const hours = {};
                            for (const r of sortRows) for (const [hh, v] of Object.entries(r.hours || {})) hours[hh] = (hours[hh] || 0) + (v || 0);
                            const total = sortRows.reduce((s, r) => s + (r.total || 0), 0);
                            groupFns = [...groupFns, { process_path: 'Manual Sort', group: 'EOL', hours, total }];
                          }
                        }
                      }
                      groupFns = groupFns.sort((a,b) => b.total - a.total);
                      if (groupFns.length === 0) return null;
                      // Group header aggregate EXCLUDES the synthetic Manual Sort row: its
                      // hours span EOL + AROS sort but the group basis (cartonForGroup /
                      // computedHours[group]) is EOL-only, so including it would skew the EOL
                      // group's CPLH / delta / %. Manual Sort still renders as its own row
                      // (with the combined eol+aros basis from FN_CARTON_BASIS).
                      const aggFns = groupFns.filter(l => l.process_path !== 'Manual Sort');
                      // Degenerate case: the group has ONLY the synthetic Manual Sort row (no
                      // genuine EOL functions this shift). There is no clean group-level carton
                      // or plan-hours basis for a sort-only header (computedHours is keyed by
                      // group, not by the fused sort labor), so we don't fabricate one: show the
                      // Manual Sort actual hours in the header total and leave the plan-derived
                      // cells (Goal Hours, plan rate, delta, %) blank. The Manual Sort row itself
                      // still carries the correct combined eol+aros CPLH via FN_CARTON_BASIS.
                      const manualSortOnly = aggFns.length === 0;
                      const groupTotal = (manualSortOnly ? groupFns : aggFns).reduce((s,l) => s + l.total, 0);
                      const cartons = manualSortOnly ? 0 : cartonForGroup(group);
                      const planCartons = manualSortOnly ? 0 : planCartonForGroup(group);
                      // Prorate plan hours to volume processed so far this shift:
                      // effective = full plan hours * (actual cartons / plan cartons).
                      const groupProrate = planCartons > 0 ? (cartons / planCartons) : 0;
                      const groupRate = (!manualSortOnly && groupTotal > 0) ? cartons / groupTotal : 0;
                      const groupFullPlanHours = manualSortOnly ? 0 : (lvpPlan?.computedHours?.[group]?.[lvpPlan?.shiftKey] || 0);
                      const groupPlanHours = groupFullPlanHours * groupProrate;
                      const groupPlanRate = groupPlanHours > 0 ? planCartons / groupFullPlanHours : 0;
                      const groupPct = groupPlanRate > 0 ? (groupRate/groupPlanRate*100) : 0;
                      const groupHoursDelta = groupPlanHours ? groupTotal - groupPlanHours : 0;
                      return (
                        <React.Fragment key={group + (gd.arosSide||'')}>
                          <tr style={{ background: '#e3f2fd', borderTop: '2px solid #000' }}>
                            <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>{gd.label || group}</td>
                            <td style={{ ...cell, fontWeight: 700 }}>{groupPlanHours ? Math.round(groupPlanHours) : '—'}</td>
                            <td style={{ ...cell, fontWeight: 700 }}>{groupPlanRate ? groupPlanRate.toFixed(1) : '—'}</td>
                            <td style={{ ...cell, fontWeight: 700 }}>{Math.round(groupTotal)}</td>
                            <td style={{ ...cell, fontWeight: 700 }}>{groupRate ? groupRate.toFixed(1) : '—'}</td>
                            <td style={{ ...cell, fontWeight: 700, background: groupPlanHours ? (groupHoursDelta <= 0 ? '#e2efda' : '#fbe4e4') : undefined }}>{groupPlanHours ? (groupHoursDelta > 0 ? '+' : '') + Math.round(groupHoursDelta) : '—'}</td>
                            <td style={{ ...cell, fontWeight: 700, background: groupPct ? (groupPct >= 100 ? '#e2efda' : '#fbe4e4') : undefined }}>{groupPct ? `${groupPct.toFixed(0)}%` : '—'}</td>
                          </tr>
                          {groupFns.map(fn => {
                            // Per-function carton basis for CPLH: some Fluid functions
                            // are rated against Fluid Direct vs Fluid Offline specifically,
                            // not the whole Fluid total.
                            //   Sorter Wallbuild            -> Fluid Direct
                            //   Offline Wallbuild, Fluid Load -> Fluid Offline
                            const dActF = fclmData?.cartons?.fluidDirect?.total || 0;
                            const oActF = fclmData?.cartons?.fluidOffline?.total || 0;
                            const dPlanF = lvpPlan ? Math.round(lvpPlan.pidCartons * (1 - (lvpPlan.eolPct||33)/100)) : 0;
                            const oPlanF = lvpPlan ? Math.max(0, (lvpPlan.pidCartons||0) + (lvpPlan.arosCartons||0) - dPlanF) : 0;
                            // Manual Sort: fused EOL Sort + AROS Sort. Its carton basis is the
                            // COMBINED eol + aros total (the only measurable basis for the fused
                            // line); the other EOL/OB-AROS functions keep their own group stream.
                            const eolActMS = fclmData?.cartons?.eol?.total || 0;
                            const arosActMS = fclmData?.cartons?.aros?.total || 0;
                            const eolPlanMS = lvpPlan ? Math.round((lvpPlan.pidCartons||0) * (lvpPlan.eolPct||33)/100) : 0;
                            const arosPlanMS = lvpPlan ? (lvpPlan.arosCartons || 0) : 0;
                            const FN_CARTON_BASIS = {
                              'Sorter Wallbuild': { act: dActF, plan: dPlanF },
                              'Offline Wallbuild': { act: oActF, plan: oPlanF },
                              'Fluid Load': { act: oActF, plan: oPlanF },
                              'Manual Sort': { act: eolActMS + arosActMS, plan: eolPlanMS + arosPlanMS },
                            };
                            const basis = FN_CARTON_BASIS[fn.process_path];
                            const fnActCartons = basis ? basis.act : cartons;
                            const fnPlanCartons = basis ? basis.plan : planCartons;
                            const fnRate = fn.total > 0 ? fnActCartons / fn.total : 0;
                            const lvpKey = fclmToLvp[fn.process_path] || fn.process_path;
                            const fnFullPlanHours = (lvpPlan?.computedHours?.[lvpKey]?.[lvpPlan?.shiftKey]) || (lvpPlan?.computedHours?.[fn.process_path]?.[lvpPlan?.shiftKey]) || 0;
                            // Prorate plan hours to this function's volume processed so far.
                            const fnProrate = fnPlanCartons > 0 ? (fnActCartons / fnPlanCartons) : 0;
                            const planHours = fnFullPlanHours * fnProrate;
                            const planRate = fnFullPlanHours > 0 ? fnPlanCartons / fnFullPlanHours : 0;
                            const pctToPlan = planRate > 0 ? (fnRate/planRate*100) : 0;
                            const hoursDelta = planHours ? fn.total - planHours : 0;
                            return (
                              <tr key={fn.process_path} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ ...cell, textAlign: 'left', paddingLeft: 16 }}>{fn.process_path}</td>
                                <td style={cell}>{planHours ? planHours.toFixed(1) : '—'}</td>
                                <td style={cell}>{planRate ? planRate.toFixed(1) : '—'}</td>
                                <td style={cell}>{fn.total.toFixed(1)}</td>
                                <td style={cell}>{fnRate ? fnRate.toFixed(1) : '—'}</td>
                                <td style={{ ...cell, color: planHours ? (hoursDelta <= 0 ? '#2e7d32' : '#c62828') : '#333', fontWeight: 600 }}>{planHours ? (hoursDelta > 0 ? '+' : '') + hoursDelta.toFixed(1) : '—'}</td>
                                <td style={{ ...cell, color: pctToPlan >= 100 ? '#2e7d32' : pctToPlan > 0 ? '#c62828' : '#333' }}>{pctToPlan ? `${pctToPlan.toFixed(0)}%` : '—'}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Cost Bridge */}
            <div style={{ flex: '1 1 0', minWidth: 220 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>Cost Bridge</label>
              <textarea rows={14} placeholder="Notes / bridge..." value={bridge} onChange={e => setBridge(e.target.value)} style={{ width: '100%', fontSize: '0.85rem', padding: 6, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical' }} />
            </div>
          </div>

          {/* OB Trailer Fill (Fluid / OBPR only) */}
          {def.fill && obFill && (() => {
            const cats = def.fill;
            const rows = (obFill || []).filter(t => cats.includes(t.category));
            if (rows.length === 0) return null;
            const byCat = cats.map(c => {
              const items = rows.filter(t => t.category === c);
              const avgFill = items.length ? (items.reduce((s,t) => s + (t.fillPct||0), 0) / items.length) : 0;
              const cart = items.reduce((s,t) => s + (t.cartons||0), 0);
              return { c, count: items.length, avgFill, cart };
            });
            const fillColor = (f) => f >= 65 ? '#2e7d32' : f >= 55 ? '#f57f17' : '#c62828';
            return (
              <div className="card" style={{ overflowX: 'auto' }}>
                <h3 className="section-title">OB Trailer Fill — {def.label}</h3>
                <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
                  <thead><tr><th style={hdr}>Category</th><th style={hdr}>Trailers</th><th style={hdr}>Avg Fill %</th><th style={hdr}>Cartons</th></tr></thead>
                  <tbody>
                    {byCat.map(r => (
                      <tr key={r.c}><td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{r.c}</td><td style={cell}>{r.count}</td><td style={{ ...cell, fontWeight: 600, color: fillColor(r.avgFill) }}>{r.avgFill ? r.avgFill.toFixed(1) + '%' : '—'}</td><td style={cell}>{r.cart ? r.cart.toLocaleString() : '—'}</td></tr>
                    ))}
                    {cats.length > 1 && (() => {
                      const avg = rows.length ? rows.reduce((s,t)=>s+(t.fillPct||0),0)/rows.length : 0;
                      return <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}><td style={{ ...cell, textAlign: 'left' }}>TOTAL</td><td style={cell}>{rows.length}</td><td style={{ ...cell, color: fillColor(avg) }}>{avg.toFixed(1)}%</td><td style={cell}>{rows.reduce((s,t)=>s+(t.cartons||0),0).toLocaleString()}</td></tr>;
                    })()}
                  </tbody>
                </table>
                {/* Per-category expandable detail (all trailers), like FALLIN */}
                {cats.map(cat => {
                  const cl = rows.filter(t => t.category === cat).sort((a,b) => (b.fillPct||0)-(a.fillPct||0));
                  if (cl.length === 0) return null;
                  return (
                    <details key={cat} style={{ marginBottom: 6 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem' }}>{cat} — {cl.length} loads (avg {(cl.reduce((s,t)=>s+(t.fillPct||0),0)/cl.length).toFixed(1)}% fill)</summary>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.6rem', marginTop: 4 }}>
                        <thead><tr>{['Door','VRID','Time','Fill %','Cubic Vol','Cartons'].map(h => <th key={h} style={{ ...hdr, fontSize: '0.55rem' }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {cl.map((t, i) => (
                            <tr key={i} style={{ background: i%2===0 ? '#fff' : '#f9f9f9' }}>
                              <td style={{ ...cell, fontSize: '0.55rem' }}>{t.door || '—'}</td>
                              <td style={{ ...cell, fontSize: '0.55rem' }}>{idLink(t.vrid || '—', t.visitId)}</td>
                              <td style={{ ...cell, fontSize: '0.55rem' }}>{t.completionTimePST?.split(' ')[1] || '—'}</td>
                              <td style={{ ...cell, fontSize: '0.55rem', fontWeight: 600, color: fillColor(t.fillPct||0) }}>{t.fillPct?.toFixed ? t.fillPct.toFixed(1) : t.fillPct}%</td>
                              <td style={{ ...cell, fontSize: '0.55rem' }}>{t.cubicVolume ? Math.round(t.cubicVolume).toLocaleString() : '—'}</td>
                              <td style={{ ...cell, fontSize: '0.55rem' }}>{t.cartons ? Math.round(t.cartons).toLocaleString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  );
                })}
              </div>
            );
          })()}

          {/* Trailer plans for this dept's paths — same layout as MIDI */}
          {def.paths.map(path => {
            const completions = getCompletions(path);
            const planTrailers = planTrailersFor(path);
            const sosTime = midiPlan?.frozen ? new Date(midiPlan.frozen).getTime() / 1000 : 0;
            const getDwellAtSos = (c) => { if (!c || !c.checkInTime || !sosTime) return c?.dwellHours || 0; return (sosTime - c.checkInTime) / 3600; };
            const planVids = new Set(planTrailers.map(t => normalizeVid(t.visitId)));
            const rows = [];
            const unmatchedActuals = [];
            for (const pt of planTrailers) {
              const matchedComp = completions.find(c => normalizeVid(c.visitId) === normalizeVid(pt.visitId));
              rows.push({ plan: pt, actual: matchedComp || null });
            }
            for (const c of completions) { if (!planVids.has(normalizeVid(c.visitId))) unmatchedActuals.push(c); }
            if (planTrailers.length === 0 && completions.length === 0) return null;
            return (
              <div key={path} className="card" style={{ marginBottom: 8, overflowX: 'auto' }}>
                <h4 style={{ margin: '4px 0', fontSize: '0.8rem' }}>{path} — Plan: {planTrailers.length} trailers | Actual: {completions.length}</h4>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.65rem', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#232f3e', color: '#fff' }}>
                      <th colSpan={4} style={{ padding: '3px 6px', textAlign: 'center', borderRight: '2px solid #fff' }}>Plan</th>
                      <th colSpan={5} style={{ padding: '3px 6px', textAlign: 'center' }}>Actuals</th>
                    </tr>
                    <tr style={{ background: '#333', color: '#fff' }}>
                      <th style={headerStyle}>Location</th><th style={headerStyle}>ISA</th><th style={headerStyle}>Dwell</th><th style={{ ...headerStyle, borderRight: '2px solid #000' }}>Cartons</th>
                      <th style={headerStyle}>Location</th><th style={headerStyle}>ISA</th><th style={headerStyle}>Dwell</th><th style={headerStyle}>In Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const pt = row.plan; const ct = row.actual;
                      const rowBg = ct ? '#f5f5f5' : '#fff3e0';
                      return (
                        <tr key={i} style={{ background: rowBg }}>
                          <td style={cellStyle}>{pt.location}</td>
                          <td style={cellStyle}>{idLink(pt.vehicle || normalizeVid(pt.visitId), pt.visitId)}</td>
                          <td style={cellStyle}>{pt.dwellHours ? (pt.dwellHours/24).toFixed(1) + 'd' : '—'}</td>
                          <td style={{ ...cellStyle, borderRight: '2px solid #000' }}>{pt.cartons ? pt.cartons.toLocaleString() : '—'}</td>
                          <td style={cellStyle}>{ct?.location || ''}</td>
                          <td style={cellStyle}>{ct ? idLink(ct.isaId || ct.vehicle || normalizeVid(ct.visitId), ct.visitId) : ''}</td>
                          <td style={cellStyle}>{ct ? (getDwellAtSos(ct)/24).toFixed(1) + 'd' : ''}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: ct ? '#4caf50' : '#e65100' }}>{ct ? 'Yes' : 'Not Closed'}</td>
                        </tr>
                      );
                    })}
                    {unmatchedActuals.map((c, i) => {
                      const loc = c.location || '';
                      const ddNum = loc.startsWith('DD') ? parseInt(loc.slice(2)) : 0;
                      const isPidDoor = [352,353,354,355,357,358,359,360].includes(ddNum);
                      const isPrDoor = [143,146,150,153,157].includes(ddNum);
                      const isManrDoor = [347,348].includes(ddNum);
                      const doorPath = isPidDoor ? 'PID' : isPrDoor ? 'PR' : isManrDoor ? 'MANR' : 'SKID';
                      if (doorPath !== path && c.path === path) return null;
                      if (doorPath !== path && c.path !== path) return null;
                      const MISSED_BUFFER_HOURS = 24;
                      const shortestPlanDwell = planTrailers.length > 0 ? (planTrailers[planTrailers.length - 1].dwellHours || 0) : 0;
                      const dwellAtSos = getDwellAtSos(c);
                      let status;
                      if (doorPath !== path) status = `✓ ${doorPath}`;
                      else if (shortestPlanDwell > 0 && dwellAtSos > shortestPlanDwell + MISSED_BUFFER_HOURS) status = '⚠️ Missed';
                      else status = 'Not in Plan';
                      const color = status.startsWith('✓') || status === '⚠️ Missed' ? '#4caf50' : '#c62828';
                      return (
                        <tr key={'u' + i} style={{ background: status.startsWith('✓') || status === '⚠️ Missed' ? '#e8f5e9' : '#ffcdd2' }}>
                          <td style={cellStyle}></td><td style={cellStyle}></td><td style={cellStyle}></td><td style={{ ...cellStyle, borderRight: '2px solid #000' }}></td>
                          <td style={cellStyle}>{c.location}</td>
                          <td style={cellStyle}>{idLink(c.isaId || c.vehicle || normalizeVid(c.visitId), c.visitId)}</td>
                          <td style={cellStyle}>{c.dwellHours ? (getDwellAtSos(c)/24).toFixed(1) + 'd' : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color }}>{status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
